import { MatTableDataSource } from '@angular/material/table'
import { ChangeDetectionStrategy, Component, HostBinding, OnInit, ViewChild, inject } from '@angular/core'
import { FormControl, FormGroup, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Actions, ofType } from '@ngrx/effects'
import { Store, select } from '@ngrx/store'
import { Deal, DealViewRawCosts, DealViewRawDeal, LocationObject } from '@tradecafe/types/core'
import { DeepReadonly } from '@tradecafe/types/utils'
import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed'
import { pick, range } from 'lodash-es'
import { BehaviorSubject, ObservedValueOf, combineLatest } from 'rxjs'
import { map, take } from 'rxjs/operators'
import { cloneDeal, cloneDealFailure, cloneDealSuccess } from 'src/app/store/deal-view.actions'
import { loadLocations, selectAllLocations, selectLocationEntities } from 'src/app/store/locations'
import { ConfirmModalService } from 'src/components/confirm/confirm-modal.service'
import { CANCELED, InlineEditorComponent } from 'src/components/inline-editor/inline-editor.component'
import { hasDealStaleData } from 'src/services/data/deal-view.service'
import { dayjs } from 'src/services/dayjs'
import { replayForm } from 'src/shared/utils/replay-form'
import { waitNotEmpty } from 'src/shared/utils/wait-not-empty'


export interface CloneFormOptions {
  dealViewRaw: DeepReadonly<DealViewRawDeal & DealViewRawCosts>
}

@Component({
    selector: 'tc-clone-form',
    templateUrl: './clone-form.component.html',
    styleUrl: './clone-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CloneFormComponent extends OnDestroyMixin implements OnInit {
  private readonly ConfirmModal = inject(ConfirmModalService)
  private readonly store = inject(Store)
  private readonly actions$ = inject(Actions)
  private readonly dialogRef = inject<MatDialogRef<CloneFormComponent, DeepReadonly<Deal[]>>>(MatDialogRef)
  private readonly dialogData = inject<CloneFormOptions>(MAT_DIALOG_DATA)


  // const
  protected readonly minDate = dayjs().unix()
  protected readonly dealId = this.dialogData.dealViewRaw.deal.deal_id

  // ref data
  protected readonly locations$ = this.store.pipe(select(selectAllLocations), waitNotEmpty())
  protected readonly locationsById$ = this.store.pipe(select(selectLocationEntities), waitNotEmpty())

  // form
  protected readonly cloneForm = new FormGroup({
    count: new FormControl<number>(undefined, [Validators.required, Validators.min(1)]),
    multiple: new FormControl<number>(undefined, [Validators.required, Validators.min(1)]),
    frequency: new FormControl<number>(undefined, [Validators.required, Validators.min(0)]),
    shipmentStart: new FormControl<number>(undefined),
  })

  // table data
  protected readonly data$ = combineLatest([replayForm(this.cloneForm), this.locationsById$]).pipe(map(([cloneForm, locations]) =>
    this.cloneForm.valid ? this.refreshCloneList(cloneForm, locations) : []))
  protected readonly dataSource = new MatTableDataSource<ObservedValueOf<typeof this.data$>[number]>([])
  protected readonly displayColumns = ['clone_sequence', 'origin_name', 'shipment_dates', 'delivery_dates', 'fx_rate_bid', 'fx_rate_ask']

  // state
  protected readonly inProgress$ = new BehaviorSubject<'loading'|'save'|undefined>('loading')

  // dom interactions
  @ViewChild(InlineEditorComponent) protected inlineEditor: InlineEditorComponent
  @HostBinding('style.--rows-visible') get visibleRowsCount() { return this.dataSource.data.length }


  ngOnInit() {
    this.store.dispatch(loadLocations({}))

    this.locations$.pipe(take(1)).subscribe(() =>
      this.inProgress$.next(undefined))
    this.data$.pipe(untilComponentDestroyed(this)).subscribe(data => {
      this.dataSource.data = data
    })
  }

  protected async editPickupLocation(row: ObservedValueOf<typeof this.data$>[number], cellEl: HTMLElement) {
    if (!row.attributes.clone_sequence) return // don't edit original deal
    combineLatest([this.locations$, this.locationsById$]).pipe(take(1)).subscribe(async ([locations, locationsById]) => {
      try {
        const result = await this.inlineEditor.selectOption({
          items: locations,
          bindValue: 'location_id',
          bindLabel: 'name',
        }, () => row.origin_location, cellEl)
        row.origin_location = result
        row.origin = locationsById[result]
      } catch(err) {
        if (err !== CANCELED) throw err
      }
    })
  }

  protected async editShipmentDates(row: ObservedValueOf<typeof this.data$>[number], cellEl: HTMLElement) {
    if (!row.attributes.clone_sequence) return // don't edit original deal
    const deal = this.dialogData.dealViewRaw.deal
    try {
      const result = await this.inlineEditor.pickDateRange(cellEl, () => ({
        from: row.attributes.shipment_dates.from,
        to: row.attributes.shipment_dates.to,
        tbd: row.attributes.shipment_dates.tbd,
        from2: row.attributes.delivery_dates.from,
        to2: row.attributes.delivery_dates.to,
        min: deal.attributes.deal_date ||
            deal.created ||
            dayjs().utc().startOf('day').unix(),
        max: row.attributes.delivery_dates.from,
        useUtc: true,
      }))
      row.attributes.shipment_dates = result
    } catch(err) {
      if (err !== CANCELED) throw err
    }
  }

  protected async editDeliveryDates(row: ObservedValueOf<typeof this.data$>[number], cellEl: HTMLElement) {
    if (!row.attributes.clone_sequence) return // don't edit original deal
    const deal = this.dialogData.dealViewRaw.deal
    try {
      const result = await this.inlineEditor.pickDateRange(cellEl, () => ({
        from: row.attributes.delivery_dates.from,
        to: row.attributes.delivery_dates.to,
        tbd: row.attributes.delivery_dates.tbd,
        from2: row.attributes.shipment_dates.from,
        to2: row.attributes.shipment_dates.to,
        min: row.attributes.shipment_dates.from ||
            deal.attributes.deal_date ||
            deal.created ||
            dayjs().utc().startOf('day').unix(),
        useUtc: true,
      }))
      row.attributes.delivery_dates = result
    } catch(err) {
      if (err !== CANCELED) throw err
    }
  }

  protected async save() {
    if (hasDealStaleData(this.dialogData.dealViewRaw)) {
      await this.ConfirmModal.showStaleDataWarning()
    }

    const deal = this.dialogData.dealViewRaw.deal
    const clonedItems = this.dataSource.data
    const payload = clonedItems.slice(1).map(row => ({
      // TODO: WA-13910: update dealmanager to support dateRange.tbd field
      shipment_dates: {
        from: row.attributes.shipment_dates.from,
        to: row.attributes.shipment_dates.to,
      },
      delivery_dates: {
        from: row.attributes.delivery_dates.from,
        to: row.attributes.delivery_dates.to,
      },
      location_id: row.origin_location,
    }))

    this.inProgress$.next('save')
    this.store.dispatch(cloneDeal({ dealId: deal.deal_id, payload }))
    this.actions$.pipe(ofType(cloneDealSuccess, cloneDealFailure), take(1)).subscribe(action => {
      this.inProgress$.next(undefined)
      if (action.type === cloneDealSuccess.type) {
        this.dialogRef.close(action.deals)
      }
    })
  }

  protected getRowId(_index: number, row: ObservedValueOf<typeof this.data$>[number]) {
    return row.attributes.clone_sequence
  }

  private refreshCloneList(
    { count, multiple, frequency, shipmentStart }: ReturnType<typeof this.cloneForm.getRawValue>,
    locations: DeepReadonly<Dictionary<LocationObject>>,
  ) {
    const deal = this.dialogData.dealViewRaw.deal

    return range(0, count * multiple).map(index =>
      buildCloneItem(index, shipmentStart, frequency, Math.floor(index / multiple)))

    function buildCloneItem(clone_sequence: number, shipmentStart: number, frequency: number, periodIndex: number) {
      const days = 24 * 3600

      let { shipment_dates, delivery_dates } = deal.attributes

      if (shipmentStart || frequency) {
        shipmentStart = shipmentStart
          ? dayjs.unix(shipmentStart).utc().startOf('day').unix()
          : deal.attributes.shipment_dates.from

        const dealPeriod = deal.attributes.delivery_dates.from - deal.attributes.shipment_dates.from
        const deliveryInterval = deal.attributes.delivery_dates.to - deal.attributes.delivery_dates.from
        const shipmentInterval = deal.attributes.shipment_dates.to - deal.attributes.shipment_dates.from

        shipment_dates = {
          from: shipmentStart + periodIndex * frequency * days,
          to: shipmentStart + shipmentInterval + periodIndex * frequency * days,
        }
        delivery_dates = {
          from: shipmentStart + dealPeriod + periodIndex * frequency * days,
          to: shipmentStart + dealPeriod + deliveryInterval + periodIndex * frequency * days,
        }
      }

      return {
        origin: locations[deal.origin_location],
        origin_location: deal.origin_location,
        attributes: {
          ...pick(deal.attributes, [
            'fx_rates',
            'estimated',
            'fx_rates_bid_range',
            'fx_rates_ask_range',
          ]),
          clone_sequence,
          shipment_dates,
          delivery_dates,
        },
      }
    }
  }
}
